/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { CommandBarButton } from '@fluentui/react/lib/Button';
import { ICommandBarItemProps } from '@fluentui/react/lib/CommandBar';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisCommandBar } from 'src/core/components/LuisCommandBar';
import { RBACTooltip } from 'src/core/components/RBACTooltip';
import { TableCommandBarProps } from 'src/core/components/TableCommandBarProps';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getCommandBarButtonStyles } from 'src/core/utils/azureRbacStyles';
import { mapUserRoleToUserActions } from 'src/core/utils/azureUtils';
import { commandBarItemSelectionManager } from 'src/core/utils/commandBarItemSelectionManager';
import { Intent } from 'src/luis/modules/intents/Intent';

export type IntentTableCommandBarProps = TableCommandBarProps<Intent> & {
    /**
     * Callback for creating an intent.
     */
    onCreateIntent?: () => boolean | void;

    /**
     * Callback for adding adding a prebuilt intent.
     */
    onAddPrebuiltIntent?: () => boolean | void;

    /**
     * Callback for renaming an intent.
     */
    onRenameIntent?: () => boolean | void;

    /**
     * Callback for deleting intents.
     */
    onDeleteIntent?: () => boolean | void;

    /**
     * User Role to extract user actions.
     */
    userRole: string;

    /**
     * Role to display in the luis tool tip messages.
     */
    displayedUserRole: string;
};

const messages = defineMessages({
    searchPlaceholder: {
        id: 'intents.intentTableCommandBar.searchPlaceholder',
        defaultMessage: 'Search for an intent by name ...'
    },
    createIntentButton: {
        id: 'intents.intentTableCommandBar.createIntentButton',
        defaultMessage: 'Create'
    },
    addPrebuiltIntentButton: {
        id: 'intents.intentTableCommandBar.addPrebuiltIntentButton',
        defaultMessage: 'Add prebuilt domain intent'
    },
    renameIntentButton: {
        id: 'intents.intentTableCommandBar.renameIntentButton',
        defaultMessage: 'Rename'
    },
    deleteIntentButton: {
        id: 'intents.intentTableCommandBar.deleteIntentButton',
        defaultMessage: 'Delete'
    }
});

/**
 * Represents a command bar for the intent table.
 *
 * @param props The properties for the intent table command bar.
 */
export const IntentTableCommandBar = (props: IntentTableCommandBarProps) => {
    const { selectedItems = [], query = '', onQueryChange, userRole, displayedUserRole } = props;
    const { localize } = useLocalization();
    const { canDeleteData, canWrite } = mapUserRoleToUserActions(userRole);

    const commands: ICommandBarItemProps[] = [
        {
            key: 'create',
            onRender: (item: ICommandBarItemProps) => (
                <RBACTooltip
                    hidden={canWrite || item.disabled}
                    displayedUserRole={displayedUserRole}
                    data-automation-id="createIntentButtonLuisToolTip"
                    componentSurroundedByToolTip={
                        <CommandBarButton
                            onClick={props.onCreateIntent}
                            iconProps={{ iconName: 'Add' }}
                            disabled={!canWrite || item.disabled}
                            data-automation-id="createIntentButton"
                            text={localize(messages.createIntentButton)}
                            name={localize(messages.createIntentButton)}
                            styles={getCommandBarButtonStyles(item.renderedInOverflow)}
                        />
                    }
                />
            )
        },
        {
            key: 'addPrebuilt',
            onRender: (item: ICommandBarItemProps) => (
                <RBACTooltip
                    hidden={canWrite || item.disabled}
                    displayedUserRole={displayedUserRole}
                    data-automation-id="addPreBuiltIntentButtonLuisToolTip"
                    componentSurroundedByToolTip={
                        <CommandBarButton
                            iconProps={{ iconName: 'Add' }}
                            onClick={props.onAddPrebuiltIntent}
                            disabled={!canWrite || item.disabled}
                            data-automation-id="addPreBuiltIntentButton"
                            text={localize(messages.addPrebuiltIntentButton)}
                            name={localize(messages.addPrebuiltIntentButton)}
                            styles={getCommandBarButtonStyles(item.renderedInOverflow)}
                        />
                    }
                />
            )
        },
        {
            key: 'rename',
            onRender: (item: ICommandBarItemProps) => (
                <RBACTooltip
                    hidden={canWrite || item.disabled}
                    displayedUserRole={displayedUserRole}
                    data-automation-id="renameIntentButtonLuisToolTip"
                    componentSurroundedByToolTip={
                        <CommandBarButton
                            onClick={props.onRenameIntent}
                            iconProps={{ iconName: 'Rename' }}
                            disabled={!canWrite || item.disabled}
                            data-automation-id="renameIntentButton"
                            text={localize(messages.renameIntentButton)}
                            name={localize(messages.renameIntentButton)}
                            styles={getCommandBarButtonStyles(item.renderedInOverflow)}
                        />
                    }
                />
            )
        },
        {
            key: 'delete',
            onRender: (item: ICommandBarItemProps) => (
                <RBACTooltip
                    displayedUserRole={displayedUserRole}
                    hidden={canDeleteData || item.disabled}
                    data-automation-id="deleteIntentButtonLuisToolTip"
                    componentSurroundedByToolTip={
                        <CommandBarButton
                            onClick={props.onDeleteIntent}
                            iconProps={{ iconName: 'Delete' }}
                            data-automation-id="deleteIntentButton"
                            disabled={!canDeleteData || item.disabled}
                            text={localize(messages.deleteIntentButton)}
                            name={localize(messages.deleteIntentButton)}
                            styles={getCommandBarButtonStyles(item.renderedInOverflow)}
                        />
                    }
                />
            )
        }
    ];

    const items: ICommandBarItemProps[] = commandBarItemSelectionManager(
        commands,
        ['create', 'addPrebuilt'],
        ['rename', 'delete'],
        ['delete'],
        selectedItems.length
    );

    return <LuisCommandBar items={items} query={query} onQueryChange={onQueryChange} includeSearchBar searchBarAlwaysExpanded />;
};
